import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";

import { getRelevantSideEffects } from "@/domains/profile/models/constants";
import {
  hpFeedbackAnswerTypeSchema,
  hpFeedbackFormSchema,
  otpVerifiedUserSchema,
} from "@/domains/profile/models/schemas";
import type { HpFeedbackForm } from "@/domains/profile/models/types";
import { resetAnswerValue } from "@/domains/profile/utils/formsort";
import type { FeedbackQuestion } from "./-components/product-feedback-step";
import { ProductFeedbackStep } from "./-components/product-feedback-step";
import { ProductSelectionStep } from "./-components/product-selection-step";
import { useCustomQuestionAutoheight } from "./-hooks/use-custom-question-autoheight";
import { useFlowAnswer } from "./-hooks/use-flow-answer";

const emptyFormValues: HpFeedbackForm = { selectedProducts: [], skipped: false, feedback: {} };

type QuestionStep = { sku: string; question: FeedbackQuestion };

const buildQuestionSteps = (
  selectedProducts: string[],
  feedback: HpFeedbackForm["feedback"],
): QuestionStep[] => {
  return selectedProducts.flatMap((sku) => {
    const steps: QuestionStep[] = [
      { sku, question: "dosage" },
      { sku, question: "relief" },
      { sku, question: "sideEffects" },
    ];

    const productFeedback = feedback[sku];

    if (
      productFeedback?.hasSideEffects &&
      getRelevantSideEffects(productFeedback.sideEffects).length > 0
    ) {
      steps.push({ sku, question: "sideEffectsIntensity" });
    }

    return steps;
  });
};

const Feedback = () => {
  const { containerRef, scrollToTop } = useCustomQuestionAutoheight();
  const verifiedUser = useFlowAnswer("otp_check", otpVerifiedUserSchema);
  const previousAnswer = useFlowAnswer("hp_product_feedback", hpFeedbackAnswerTypeSchema);
  const [step, setStep] = useState(0);

  const previousFormValues = useMemo<HpFeedbackForm | undefined>(() => {
    if (!previousAnswer) {
      return undefined;
    }

    return {
      selectedProducts: previousAnswer.hp_selected_products,
      skipped: previousAnswer.skipped,
      feedback: Object.fromEntries(
        previousAnswer.hp_product_feedbacks.map(
          ({
            has_unwanted_side_effects,
            how_much_relief,
            how_much_typically_take_it,
            how_much_typically_take_it_id,
            side_effect_intensities,
            sku,
            unwanted_side_effects,
          }) => {
            return [
              sku,
              {
                dosage: how_much_typically_take_it,
                dosageId: how_much_typically_take_it_id,
                relief: how_much_relief,
                hasSideEffects: has_unwanted_side_effects,
                sideEffects: unwanted_side_effects ?? [],
                sideEffectIntensities: Object.fromEntries(
                  (side_effect_intensities ?? []).map(({ intensity, side_effect }) => {
                    return [side_effect, intensity];
                  }),
                ),
              },
            ];
          },
        ),
      ),
    };
  }, [previousAnswer]);

  const form = useForm<HpFeedbackForm>({
    resolver: zodResolver(hpFeedbackFormSchema),
    values: previousFormValues ?? emptyFormValues,
  });

  const selectedProducts = form.watch("selectedProducts");
  const feedback = form.watch("feedback");

  const questionSteps = buildQuestionSteps(selectedProducts, feedback);

  const handleContinue = () => {
    const { feedback: answers } = form.getValues();
    const steps = buildQuestionSteps(selectedProducts, answers);

    if (step < steps.length) {
      setStep((s) => {
        return s + 1;
      });
      scrollToTop();

      return;
    }

    const hp_product_feedbacks = selectedProducts.map((sku) => {
      const item = answers[sku];
      const intensities = item.sideEffectIntensities ?? {};
      const side_effect_intensities = getRelevantSideEffects(item.sideEffects)
        .filter((sideEffect) => {
          return Boolean(intensities[sideEffect]);
        })
        .map((sideEffect) => {
          return { side_effect: sideEffect, intensity: intensities[sideEffect] };
        });

      return {
        sku,
        how_much_typically_take_it: item.dosage,
        how_much_typically_take_it_id: item.dosageId,
        how_much_relief: item.relief,
        has_unwanted_side_effects: item.hasSideEffects,
        unwanted_side_effects: item.sideEffects ?? [],
        side_effect_intensities,
      };
    });

    resetAnswerValue({
      skipped: false,
      hp_selected_products: selectedProducts,
      hp_product_feedbacks,
    });
  };

  if (!verifiedUser) {
    // ref is needed here to sync with the ResizeObserver in useCustomQuestionAutoheight.
    return <div ref={containerRef} />;
  }

  const currentStep = step > 0 ? questionSteps[step - 1] : undefined;
  const currentProduct = currentStep
    ? verifiedUser.products.find((p) => {
        return p.sku === currentStep.sku;
      })
    : undefined;

  return (
    <div ref={containerRef}>
      <FormProvider {...form}>
        <div className="flex flex-col gap-4 p-1">
          {step === 0 ? (
            <ProductSelectionStep
              onContinue={() => {
                return setStep(1);
              }}
              products={verifiedUser.products}
            />
          ) : (
            currentStep &&
            currentProduct && (
              <ProductFeedbackStep
                key={`${currentStep.sku}-${currentStep.question}`}
                onContinue={handleContinue}
                product={currentProduct}
                question={currentStep.question}
              />
            )
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export const Route = createFileRoute("/_guest/profiling/_custom-questions/hp-feedback")({
  component: Feedback,
});
